import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Typography, 
  Grid, 
  Paper,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  Tooltip,
  Divider,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { RpcConfig, LogLevel, CreateLogParams } from '../../../types/dex';
import { useDexService } from '../../../contexts/DexServiceContext';
import { ethers } from 'ethers';

interface RpcConfigSectionProps {
  config: RpcConfig;
  onChange: (config: RpcConfig) => void;
}

// 预设网络列表
const PRESET_NETWORKS = [
  {
    name: 'Binance Smart Chain',
    chainId: 56,
    url: 'https://bsc-mainnet.nodereal.io/v1/4b06d775febe481fbc614202d940a7cf',
    symbol: 'BNB'
  },
  
];

/**
 * RPC配置区域组件
 * 用于配置区块链网络连接参数
 */
const RpcConfigSection: React.FC<RpcConfigSectionProps> = ({ config, onChange }) => {
  const { addLog } = useDexService();
  
  // 所有网络列表
  const [networks, setNetworks] = useState<RpcConfig[]>([]);
  // 当前选中的网络索引
  const [selectedNetworkIndex, setSelectedNetworkIndex] = useState<number>(-1);
  // 新网络表单
  const [newNetwork, setNewNetwork] = useState<RpcConfig>({
    chainId: 0,
    url: '',
    name: '',
    symbol: ''
  });
  // 编辑模式
  const [isEditMode, setIsEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  
  // 连接测试状态
  const [isValidating, setIsValidating] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    connected: boolean;
    message: string;
    blockNumber?: number;
  }>({ connected: false, message: '未连接' });
  
  // 删除确认对话框状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [networkToDelete, setNetworkToDelete] = useState<number | null>(null);

  // 初始化: 如果已有配置，将其添加到网络列表中
  useEffect(() => {
    // 从localStorage获取保存的网络列表
    const savedNetworks = localStorage.getItem('rpcNetworks');
    let initialNetworks: RpcConfig[] = [];
    
    if (savedNetworks) {
      try {
        initialNetworks = JSON.parse(savedNetworks);
        const log: CreateLogParams = {
          level: LogLevel.INFO,
          message: '已加载保存的RPC网络配置',
          data: { networkCount: initialNetworks.length }
        };
        addLog(log);
      } catch (e) {
        const log: CreateLogParams = {
          level: LogLevel.ERROR,
          message: '解析保存的RPC网络配置失败',
          data: { error: e instanceof Error ? e.message : '未知错误' }
        };
        addLog(log);
        initialNetworks = [];
      }
    }
    
    if (!savedNetworks || initialNetworks.length === 0) {
      initialNetworks = [...PRESET_NETWORKS];
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: '使用预设RPC网络配置',
        data: { networks: PRESET_NETWORKS }
      };
      addLog(log);
    }
    
    setNetworks(initialNetworks);
    
    // 检查当前配置是否匹配任何已保存的网络
    const existingIndex = initialNetworks.findIndex(
      n => n.chainId === config.chainId && n.url === config.url
    );
    
    if (existingIndex >= 0) {
      setSelectedNetworkIndex(existingIndex);
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: '已找到匹配的RPC网络配置',
        data: initialNetworks[existingIndex]
      };
      addLog(log);
    } else if (initialNetworks.length > 0) {
      setSelectedNetworkIndex(0);
      onChange(initialNetworks[0]);
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: '使用默认RPC网络配置',
        data: initialNetworks[0]
      };
      addLog(log);
    }
  }, []);

  // 保存网络列表到localStorage
  useEffect(() => {
    if (networks.length > 0) {
      localStorage.setItem('rpcNetworks', JSON.stringify(networks));
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: 'RPC网络配置已保存到本地存储',
        data: { networkCount: networks.length }
      };
      addLog(log);
    }
  }, [networks]);

  // 处理新网络表单字段变更
  const handleNewNetworkChange = (field: keyof RpcConfig) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'chainId' ? parseInt(e.target.value) || 0 : e.target.value;
    setNewNetwork(prev => ({ ...prev, [field]: value }));
  };

  // 测试RPC连接
  const handleTestConnection = async (url: string) => {
    setIsValidating(true);
    setConnectionStatus({ connected: false, message: '正在连接...' });
    
    const log: CreateLogParams = {
      level: LogLevel.INFO,
      message: '开始测试RPC连接',
      data: { url }
    };
    addLog(log);

    try {
      const provider = new ethers.providers.JsonRpcProvider(url);
      const network = await provider.getNetwork();
      const blockNumber = await provider.getBlockNumber();
      
      setConnectionStatus({ 
        connected: true, 
        message: `连接成功 (Chain ID: ${network.chainId})`, 
        blockNumber 
      });
      
      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: 'RPC连接测试成功',
        data: {
          url,
          chainId: network.chainId,
          blockNumber
        }
      };
      addLog(log);
      
      if (editIndex === null) {
        setNewNetwork(prev => ({ ...prev, chainId: network.chainId }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      setConnectionStatus({ 
        connected: false, 
        message: `连接失败: ${errorMessage}`
      });
      
      const log: CreateLogParams = {
        level: LogLevel.ERROR,
        message: 'RPC连接测试失败',
        data: {
          url,
          error: errorMessage
        }
      };
      addLog(log);
    } finally {
      setIsValidating(false);
    }
  };
  
  // 添加新网络
  const handleAddNetwork = () => {
    if (!newNetwork.name || !newNetwork.url || !newNetwork.chainId || !newNetwork.symbol) {
      const log: CreateLogParams = {
        level: LogLevel.WARNING,
        message: 'RPC网络信息不完整',
        data: newNetwork
      };
      addLog(log);
      setConnectionStatus({ 
        connected: false, 
        message: '请填写完整的网络信息'
      });
      return;
    }
    
    const exists = networks.some(
      n => n.url === newNetwork.url || (n.chainId === newNetwork.chainId && n.name === newNetwork.name)
    );
    
    if (exists) {
      const log: CreateLogParams = {
        level: LogLevel.WARNING,
        message: 'RPC网络配置已存在',
        data: newNetwork
      };
      addLog(log);
      setConnectionStatus({ 
        connected: false, 
        message: '相同的网络配置已存在'
      });
      return;
    }
    
    if (isEditMode && editIndex !== null) {
      const updatedNetworks = [...networks];
      updatedNetworks[editIndex] = newNetwork;
      setNetworks(updatedNetworks);
      
      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: 'RPC网络配置已更新',
        data: {
          index: editIndex,
          network: newNetwork
        }
      };
      addLog(log);
      
      if (editIndex === selectedNetworkIndex) {
        onChange(newNetwork);
      }
      
      setIsEditMode(false);
      setEditIndex(null);
    } else {
      const updatedNetworks = [...networks, newNetwork];
      setNetworks(updatedNetworks);
      
      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: '已添加新的RPC网络配置',
        data: newNetwork
      };
      addLog(log);
      
      if (updatedNetworks.length === 1) {
        setSelectedNetworkIndex(0);
        onChange(newNetwork);
      }
    }
    
    setNewNetwork({
      chainId: 0,
      url: '',
      name: '',
      symbol: ''
    });
    
    setConnectionStatus({ connected: false, message: '网络配置已保存' });
  };
  
  // 选择网络
  const handleSelectNetwork = (index: number) => {
    setSelectedNetworkIndex(index);
    onChange(networks[index]);
    
    const log: CreateLogParams = {
      level: LogLevel.INFO,
      message: '已切换RPC网络',
      data: networks[index]
    };
    addLog(log);
  };
  
  // 编辑网络
  const handleEditNetwork = (index: number) => {
    setNewNetwork(networks[index]);
    setIsEditMode(true);
    setEditIndex(index);
    
    const log: CreateLogParams = {
      level: LogLevel.INFO,
      message: '开始编辑RPC网络配置',
      data: networks[index]
    };
    addLog(log);
  };
  
  // 取消编辑
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditIndex(null);
    setNewNetwork({
      chainId: 0,
      url: '',
      name: '',
      symbol: ''
    });
    
    const log: CreateLogParams = {
      level: LogLevel.INFO,
      message: '已取消编辑RPC网络配置'
    };
    addLog(log);
  };
  
  // 确认删除网络
  const handleDeleteNetworkConfirm = (index: number) => {
    setNetworkToDelete(index);
    setDeleteDialogOpen(true);
    
    const log: CreateLogParams = {
      level: LogLevel.WARNING,
      message: '准备删除RPC网络配置',
      data: networks[index]
    };
    addLog(log);
  };
  
  // 删除网络
  const handleDeleteNetwork = () => {
    if (networkToDelete !== null) {
      const deletedNetwork = networks[networkToDelete];
      const updatedNetworks = [...networks];
      updatedNetworks.splice(networkToDelete, 1);
      setNetworks(updatedNetworks);
      
      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: '已删除RPC网络配置',
        data: {
          deletedNetwork,
          remainingNetworks: updatedNetworks.length
        }
      };
      addLog(log);
      
      if (networkToDelete === selectedNetworkIndex) {
        if (updatedNetworks.length > 0) {
          setSelectedNetworkIndex(0);
          onChange(updatedNetworks[0]);
          
          const log: CreateLogParams = {
            level: LogLevel.INFO,
            message: '已切换到默认RPC网络',
            data: updatedNetworks[0]
          };
          addLog(log);
        } else {
          setSelectedNetworkIndex(-1);
          onChange({
            chainId: 0,
            url: '',
            name: '',
            symbol: ''
          });
          
          const log: CreateLogParams = {
            level: LogLevel.WARNING,
            message: '已清空RPC网络配置'
          };
          addLog(log);
        }
      } else if (networkToDelete < selectedNetworkIndex) {
        setSelectedNetworkIndex(prev => prev - 1);
      }
      
      setDeleteDialogOpen(false);
      setNetworkToDelete(null);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        RPC网络配置
      </Typography>
      
      <Grid container spacing={3}>
        {/* 新网络表单 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" gutterBottom>
              {isEditMode ? '编辑网络' : '添加新网络'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="网络名称"
                  value={newNetwork.name}
                  onChange={handleNewNetworkChange('name')}
                  variant="outlined"
                  helperText="区块链网络的名称，如 Binance Smart Chain"
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="链ID"
                  type="number"
                  value={newNetwork.chainId || ''}
                  onChange={handleNewNetworkChange('chainId')}
                  variant="outlined"
                  helperText="区块链网络的Chain ID，如BSC的为56"
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="RPC URL"
                  value={newNetwork.url}
                  onChange={handleNewNetworkChange('url')}
                  variant="outlined"
                  helperText="RPC节点的URL地址"
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="代币符号"
                  value={newNetwork.symbol}
                  onChange={handleNewNetworkChange('symbol')}
                  variant="outlined"
                  helperText="例如: BNB, ETH, MATIC"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button 
                  variant="outlined" 
                  onClick={() => handleTestConnection(newNetwork.url)}
                  disabled={isValidating || !newNetwork.url}
                  startIcon={isValidating ? <CircularProgress size={20} /> : null}
                  sx={{ mr: 1 }}
                >
                  测试连接
                </Button>
                
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleAddNetwork}
                  disabled={!newNetwork.name || !newNetwork.url || !newNetwork.chainId || !newNetwork.symbol}
                  startIcon={isEditMode ? <CheckIcon /> : <AddIcon />}
                  sx={{ mr: 1 }}
                >
                  {isEditMode ? '保存修改' : '添加网络'}
                </Button>
                
                {isEditMode && (
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    onClick={handleCancelEdit}
                  >
                    取消编辑
                  </Button>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1, 
                    color: connectionStatus.connected ? 'success.main' : 'error.main'
                  }}
                >
                  {connectionStatus.message}
                  {connectionStatus.blockNumber !== undefined && (
                    <span> (当前区块: {connectionStatus.blockNumber})</span>
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* 已添加网络列表 */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            已添加的网络 ({networks.length})
          </Typography>
          {networks.length > 0 ? (
            <List>
              {networks.map((network, index) => (
                <ListItem 
                  key={index}
                  divider
                  sx={{ 
                    backgroundColor: index % 2 === 0 ? 'background.default' : 'inherit',
                    ...(selectedNetworkIndex === index && { 
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                      pl: 2
                    })
                  }}
                >
                  <Radio
                    checked={selectedNetworkIndex === index}
                    onChange={() => handleSelectNetwork(index)}
                    value={index}
                    name="network-radio-button"
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        {network.name}
                        {selectedNetworkIndex === index && (
                          <Chip 
                            label="当前选中" 
                            color="primary" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          Chain ID: {network.chainId} | Symbol: {network.symbol} | URL: {network.url.substring(0, 30)}
                          {network.url.length > 30 ? '...' : ''}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="编辑网络">
                      <IconButton 
                        edge="end" 
                        aria-label="edit"
                        onClick={() => handleEditNetwork(index)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="删除网络">
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => handleDeleteNetworkConfirm(index)}
                        disabled={networks.length <= 1 && selectedNetworkIndex === index}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              暂无网络配置，请添加网络
            </Typography>
          )}
        </Grid>
      </Grid>
      
      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除这个网络配置吗？此操作无法撤销。
            {selectedNetworkIndex === networkToDelete && (
              <Typography color="error" sx={{ mt: 1 }}>
                注意：您正在删除当前选中的网络。删除后将自动选择第一个可用网络。
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteNetwork} color="error">删除</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RpcConfigSection;
